import type {
  EscalationMatrix,
  MatrixChainFormState,
  MatrixLevelFormState,
} from '../types/escalationMatrix.types';
import type { EscalationContactRole } from '../types/escalationContact.types';
import {
  buildDefaultChainLevels,
  getDefaultMaxReminderAttempts,
  getDefaultReminderMinutes,
  MAX_ESCALATION_LEVELS,
} from '../constants/escalationMatrix.constants';
import { getConfiguredLevels } from './escalationMatrixValidation';

let levelIdCounter = 0;

export function createLevelClientId(): string {
  levelIdCounter += 1;
  return `esc-level-${levelIdCounter}-${Date.now()}`;
}

export function createEmptyLevel(
  levelNumber: number,
  defaults?: Partial<MatrixLevelFormState>
): MatrixLevelFormState {
  return {
    clientId: createLevelClientId(),
    levelNumber,
    orderIndex: levelNumber,
    role: defaults?.role ?? '',
    contactId: defaults?.contactId ?? null,
    timerMinutes: defaults?.timerMinutes ?? (defaults?.isOptional ? 0 : 15),
    isOptional: defaults?.isOptional,
  };
}

export function buildDefaultChainForm(
  criticalityBand: MatrixChainFormState['criticalityBand']
): MatrixChainFormState {
  const template = buildDefaultChainLevels(criticalityBand);
  return {
    criticalityBand,
    reminderIntervalMinutes: getDefaultReminderMinutes(criticalityBand),
    maxReminderAttempts: getDefaultMaxReminderAttempts(),
    levels: template.map(item =>
      createEmptyLevel(item.levelNumber, {
        role: item.role,
        timerMinutes: item.timerMinutes,
        isOptional: item.isOptional,
      })
    ),
  };
}

export function mapMatrixToChainForm(matrix: EscalationMatrix): MatrixChainFormState {
  const band = matrix.criticalityBand;
  const apiLevels = [...matrix.levels].sort((a, b) => a.orderIndex - b.orderIndex);

  if (!apiLevels.length) {
    return {
      ...buildDefaultChainForm(band),
      matrixId: matrix.id,
      reminderIntervalMinutes: matrix.reminderIntervalMinutes,
      maxReminderAttempts: matrix.maxReminderAttempts,
    };
  }

  const levels: MatrixLevelFormState[] = apiLevels.map(level => ({
    clientId: createLevelClientId(),
    levelNumber: level.levelNumber,
    orderIndex: level.orderIndex,
    role: level.role,
    contactId: level.contactId ?? level.contact?.id ?? null,
    timerMinutes: level.timerMinutes,
    isOptional: false,
  }));

  return {
    criticalityBand: band,
    matrixId: matrix.id,
    status: matrix.status,
    reminderIntervalMinutes: matrix.reminderIntervalMinutes,
    maxReminderAttempts: matrix.maxReminderAttempts,
    levels: renumberLevels(levels),
  };
}

export function renumberLevels(levels: MatrixLevelFormState[]): MatrixLevelFormState[] {
  return levels.map((level, index) => ({
    ...level,
    levelNumber: index + 1,
    orderIndex: index + 1,
  }));
}

export function addChainLevel(chain: MatrixChainFormState): MatrixChainFormState {
  if (chain.levels.length >= MAX_ESCALATION_LEVELS) {
    return chain;
  }

  const optionalSlots = chain.levels.filter(level => level.isOptional && !level.role);
  if (optionalSlots.length) {
    const [optionalLevel] = optionalSlots;
    return {
      ...chain,
      levels: chain.levels.map(level =>
        level.clientId === optionalLevel.clientId
          ? { ...level, isOptional: true, role: '', contactId: null, timerMinutes: 0 }
          : level
      ),
    };
  }

  const nonOptional = chain.levels.filter(level => !level.isOptional || level.role);
  return {
    ...chain,
    levels: renumberLevels([
      ...nonOptional,
      createEmptyLevel(nonOptional.length + 1, { role: '', timerMinutes: 0, isOptional: true }),
    ]),
  };
}

export function removeChainLevel(
  chain: MatrixChainFormState,
  clientId: string
): MatrixChainFormState {
  if (chain.levels.length <= 1) {
    return chain;
  }
  const target = chain.levels.find(level => level.clientId === clientId);
  if (!target || target.levelNumber === 1) {
    return chain;
  }

  const remaining = chain.levels.filter(level => level.clientId !== clientId);
  return { ...chain, levels: renumberLevels(remaining) };
}

export function buildMatrixPayload(chain: MatrixChainFormState, facilityId: number) {
  const configured = getConfiguredLevels(chain.levels);
  return {
    facilityId,
    criticalityBand: chain.criticalityBand,
    reminderIntervalMinutes: chain.reminderIntervalMinutes,
    maxReminderAttempts: chain.maxReminderAttempts,
    levels: configured.map((level, index) => ({
      levelNumber: index + 1,
      orderIndex: index + 1,
      role: level.role as EscalationContactRole,
      contactId: level.contactId,
      timerMinutes: level.timerMinutes,
    })),
  };
}
